import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { fetchOrder } from "../shop/api";
import { useCart } from "../shop/CartContext";
import { useSEO } from "../lib/seo";

export default function OrderConfirmationPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const cart = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Order Confirmation — GenieX",
    description: "Your GenieX order confirmation.",
    path: "/checkout/confirmation",
  });

  useEffect(() => {
    fetchOrder(orderId)
      .then((d) => {
        setOrder(d);
        if (d.status === "paid") cart.clear();
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  const status = order?.status || statusParam || "pending";

  return (
    <main
      data-testid="order-confirmation-page"
      className="min-h-screen bg-[var(--gx-bg)] text-white pt-32 lg:pt-40 pb-24 text-center"
    >
      <div className="max-w-[560px] mx-auto px-5">
        {loading ? (
          <p className="text-dim" data-testid="order-confirmation-loading">
            Loading your order…
          </p>
        ) : status === "paid" ? (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-6" />
            <h1 className="h2-display" data-testid="order-status-paid">
              Order confirmed
            </h1>
            <p className="lead mt-4">
              Thank you! Your order <span className="text-gold">{order?.order_number}</span> has been placed.
            </p>
          </>
        ) : status === "failed" ? (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-6" />
            <h1 className="h2-display" data-testid="order-status-failed">
              Payment failed
            </h1>
            <p className="lead mt-4">Something went wrong with your payment. No amount has been charged. Please try again.</p>
          </>
        ) : (
          <>
            <Clock className="w-14 h-14 text-amber-300 mx-auto mb-6" />
            <h1 className="h2-display" data-testid="order-status-pending">
              Payment pending
            </h1>
            <p className="lead mt-4">We&apos;re confirming your payment. This can take a few minutes.</p>
          </>
        )}

        {order && (
          <div className="text-left border border-line rounded-2xl p-5 mt-10" data-testid="order-summary">
            {order.items?.map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm py-1.5">
                <span className="text-dim">
                  {it.name} × {it.quantity}
                </span>
                <span>₹{(it.unit_price * it.quantity).toLocaleString("en-IN")}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-line pt-3 mt-2">
              <span className="text-dim">Shipping</span>
              <span>{order.shipping_fee > 0 ? `₹${Number(order.shipping_fee).toLocaleString("en-IN")}` : "FREE"}</span>
            </div>
            <div className="flex justify-between pt-2">
              <span className="text-dim">Total</span>
              <span className="text-gold font-display">₹{Number(order.total).toLocaleString("en-IN")}</span>
            </div>
            {order.tracking_number && (
              <div className="flex justify-between pt-3 mt-2 border-t border-line">
                <span className="text-dim">Tracking number</span>
                <span className="text-gold font-mono" data-testid="order-tracking-number">{order.tracking_number}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
          <Link to="/shop" data-testid="order-confirmation-shop-link" className="btn-outline">
            Continue Shopping
          </Link>
          <Link to="/" data-testid="order-confirmation-home-link" className="btn-gold">
            Back Home
          </Link>
        </div>
      </div>
    </main>
  );
}
